/**
 * inventory-atc.js — shared add-to-cart helper with stock-aware clamping.
 *
 * Shopify's /cart/add.js is all-or-nothing: request 20 when 6 exist and it 422s,
 * adding nothing. This helper reads per-variant inventory from the page's
 * `.js-inventory-data` blob, proactively clamps the quantity to what can actually
 * be added (inventory - already-in-cart), and falls back to parsing the 422
 * description if the proactive clamp is ever off. Untracked / continue-selling
 * variants are never capped.
 *
 * Usage:
 *   InventoryATC.add(variantId, qty, {
 *     sections: 'cart-drawer,cart-icon-bubble',
 *     scope: someEl,                 // element to scope the .js-inventory-data lookup
 *     onAdded:   function (r) {},     // r = { qty, clamped, available, response }
 *     onSoldOut: function () {},      // nothing could be added
 *     onError:   function (body) {}   // genuine failure
 *   });
 */
window.InventoryATC = (function () {
  function getInventory(variantId, scopeEl) {
    var root = (scopeEl && scopeEl.closest && scopeEl.closest('.product-info')) || document;
    var dataEl = root.querySelector('.js-inventory-data') || document.querySelector('.js-inventory-data');
    if (!dataEl) return null;
    var list;
    try { list = JSON.parse(dataEl.textContent); } catch (e) { return null; }
    if (!Array.isArray(list)) return null;
    for (var i = 0; i < list.length; i++) {
      if (list[i].id === variantId) return list[i];
    }
    return null;
  }

  function isCapped(inv) {
    return !!inv && inv.inventory_management === 'shopify' && inv.inventory_policy === 'deny';
  }

  function addRequest(id, qty, sections) {
    var fd = new FormData();
    fd.append('id', id);
    fd.append('quantity', qty);
    if (sections) {
      fd.append('sections', sections);
      fd.append('sections_url', window.location.pathname);
    }
    return fetch('/cart/add', {
      method: 'POST',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: fd
    });
  }

  function currentlyInCart(variantId) {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        for (var i = 0; i < items.length; i++) {
          if (items[i].id === variantId) return items[i].quantity;
        }
        return 0;
      })
      .catch(function () { return 0; });
  }

  function finish(res, addQty, clamped, available, opts) {
    if (res.ok) {
      return res.json().then(function (data) {
        if (opts.onAdded) opts.onAdded({ qty: addQty, clamped: clamped, available: available, response: data });
      });
    }
    // 422 fallback — parse the true max out of the description ("...only add 6...").
    return res.json().catch(function () { return {}; }).then(function (body) {
      var m = String(body.description || body.message || '').match(/(\d+)/);
      if (!m) { if (opts.onError) opts.onError(body); return; }
      var realMax = parseInt(m[1], 10);
      // 422 path (popup with no local inventory blob): we can't tell "all in cart"
      // apart from "sold out", so report the generic case.
      if (isNaN(realMax) || realMax <= 0) { if (opts.onSoldOut) opts.onSoldOut({ inventoryQuantity: null, inCart: null, reason: 'sold_out' }); return; }
      return addRequest(opts._id, realMax, opts.sections).then(function (res2) {
        if (res2.ok) {
          return res2.json().then(function (data) {
            if (opts.onAdded) opts.onAdded({ qty: realMax, clamped: true, available: available, response: data });
          });
        }
        if (opts.onError) opts.onError(body);
      });
    });
  }

  function add(variantId, requestedQty, opts) {
    opts = opts || {};
    opts._id = variantId;
    var inv = opts.inventory || getInventory(variantId, opts.scope);
    var capped = isCapped(inv);

    var stockPromise = capped ? currentlyInCart(variantId) : Promise.resolve(0);

    return stockPromise.then(function (inCart) {
      var addQty = requestedQty;
      var clamped = false;
      var available = null;

      if (capped) {
        available = inv.inventory_quantity;
        var remaining = Math.max(0, inv.inventory_quantity - inCart);
        if (remaining <= 0) {
          // Distinguish "you already hold all the stock" from "there is none".
          var reason = (inv.inventory_quantity > 0 && inCart >= inv.inventory_quantity) ? 'in_cart' : 'sold_out';
          if (opts.onSoldOut) opts.onSoldOut({ inventoryQuantity: inv.inventory_quantity, inCart: inCart, reason: reason });
          return;
        }
        if (requestedQty > remaining) { addQty = remaining; clamped = true; }
      }

      return addRequest(variantId, addQty, opts.sections).then(function (res) {
        return finish(res, addQty, clamped, available, opts);
      });
    }).catch(function (err) {
      if (opts.onError) opts.onError(err);
    });
  }

  return { add: add, getInventory: getInventory, isCapped: isCapped };
})();
